/**
 * Axios-инстанс с автоматическим обновлением access-токена.
 * Добавляет токен в заголовки, перехватывает 401/403 и пробует обновить accessToken через /auth/refresh.
 *
 * @module axiosInstance
 * @constant {AxiosInstance} axiosInstance — экземпляр axios с интерсепторами
 */

import axios from 'axios';

/**
 * Создание базового экземпляра axios
 * @type {import('axios').AxiosInstance}
 */
const axiosInstance = axios.create({
    baseURL: import.meta.env.VITE_APP_API_URL || '/api',
});

/** @type {boolean} — Флаг, указывает, что обновление токена уже выполняется */
let isRefreshing = false;

/**
 * Очередь запросов, ожидающих завершения обновления токена
 * @type {{ resolve: (value: unknown) => void; reject: (reason?: unknown) => void; }[]}
 */
let failedQueue = [];

/**
 * Обработка всех запросов, "зависших" во время обновления токена.
 *
 * @param {unknown} error — ошибка при обновлении токена (если есть)
 * @param {string|null} token — новый access-токен (если refresh успешен)
 */
const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

/**
 * Интерсептор исходящих запросов:
 * добавляет Authorization-заголовок, если токен есть в localStorage.
 */
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

/**
 * Интерсептор входящих ответов:
 * ловит 401/403 и пробует обновить токен, после чего повторяет исходный запрос.
 */
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Если не 401/403 или запрос уже был повторён — просто выбрасываем ошибку
        if (![401, 403].includes(error.response?.status) || originalRequest._retry) {
            return Promise.reject(error);
        }

        const accessToken = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');

        // Если токенов нет — не предпринимаем попыток обновления
        if (!accessToken || !refreshToken) {
            return Promise.reject(error);
        }

        if (originalRequest?.url?.includes('/auth/refresh')) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // Если обновление уже выполняется — ставим запрос в очередь
        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            }).then(token => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                return axiosInstance(originalRequest);
            }).catch(err => Promise.reject(err));
        }

        isRefreshing = true;

        try {
            // Запрос на обновление токенов
            const response = await axios.post(
                `${import.meta.env.VITE_APP_API_URL}/site/auth/refresh`,
                { refreshToken }
            );


            const newAccess = response.data.accessToken;
            const newRefresh = response.data.refreshToken;

            // Сохраняем новые токены
            localStorage.setItem('access_token', newAccess);
            localStorage.setItem('refresh_token', newRefresh);

            // Обновляем заголовок по умолчанию
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newAccess}`;

            // Пробуждаем запросы из очереди
            processQueue(null, newAccess);

            // Повторяем оригинальный запрос с новым токеном
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            return axiosInstance(originalRequest);

        } catch (refreshError) {
            // Если не удалось обновить — очищаем токены и отклоняем запросы
            processQueue(refreshError, null);
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            return Promise.reject(refreshError);
        } finally {
            // Сбрасываем флаг
            isRefreshing = false;
        }
    }
);

export default axiosInstance;
